'use strict';

import {
  CanvasTexture, Group,
  ImageBitmapLoader,
  ImageLoader,
  Mesh,
  PlaneGeometry,
  ShaderMaterial,
  Vector2,
} from 'three';
import vertex from '../Viewer/Shaders/vertex.glsl';
import fragment from '../Viewer/Shaders/fragment.glsl';
import gsap from 'gsap';
import {Text} from '../Text/Troika';

const kenburns = [
  new Vector2(1.0,0.0),
  new Vector2(0.0,1.0),
  new Vector2(-1.0,0.0),
  new Vector2(0.0,-1.0)
];

let loader;
if (typeof createImageBitmap !== 'undefined') {
  loader = new ImageBitmapLoader();
  loader.setOptions({imageOrientation: 'flipY'});
} else {
  // some ios browser still don't support ImageBitmaps.
  loader = new ImageLoader();
}

export class Slide {
  constructor (data,viewerWidth,viewerHeight,params) {
    this.url = data.imageUrl;
    this.viewerWidth = viewerWidth;
    this.viewerHeight = viewerHeight;
    this.params = params;
    this.group = new Group();
    this.slideWidth = null;
    this.slideHeight = null;
    this.aspect = new Vector2(1.0,1.0);

    const geometry = new PlaneGeometry(2, 2, 1, 1);
    this.material = new ShaderMaterial({
      vertexShader: vertex,
      fragmentShader: fragment,
      transparent: true,
      uniforms: {
        currentSlide: {
          value: null
        },
        uKenburns: {
          value: getRandomKenbruns()
        },
        uKenburnsDirection: {
          value: -1.0
        },
        uKenburnsValue: {
          value: 0.0
        },
        uAlpha: {
          value: 0.0
        },
        forceAnimation: {
          value: this.params.forceAnimation
        }
      }
    });
    this.layer = new Mesh(geometry,this.material);
    this.group.add(this.layer);

    //Text
    const tag = data.slogan.match(/<\/?[a-z]{1,}>/g);
    if (Array.isArray(tag)) {
      const replaced = replace(tag[0]);
      this.slogan = data.slogan.replace(/<\/?[a-z]{1,}>/g,replaced);
    } else {
      this.slogan = data.slogan;
    }

    if (this.slogan.length) {
      this.sloganText = new Text();
      this.sloganText.material.depthWrite = false;
      this.sloganText.material.depthTest = false;
      this.sloganText.material.transparent = true;
      this.sloganText.isOffscreen = this.params.isOffscreen;
      this.sloganText.text = this.slogan;
      Object.entries(this.params.text.settings).forEach(([key, value]) => {
        if (key !== 'position') {
          this.sloganText[key] = value;
        } else {
          this.sloganText.position.z = value;
        }
      });
      this.sloganText.fillOpacity = 0;
      if (this.params.forceAnimation)
        this.sloganText.outlineOpacity = 0;
      this.group.add(this.sloganText);
      this.sloganText.sync();
      this.resizeText();
    }
  }

  load() {
    return new Promise(resolve => {
      loader.load(this.url,img => {

        this.slideWidth = img.width;
        this.slideHeight = img.height;
        this.calculateAspect();
        this.layer.scale.set(1 / this.aspect.x, 1 / this.aspect.y, 1);
        this.texture = new CanvasTexture(img);
        resolve(this);
      });
    });
  }

  calculateAspect() {
    const imageAspect = this.slideWidth / this.slideHeight;
    const viewerAspect = this.viewerWidth / this.viewerHeight;

    if (viewerAspect < imageAspect) {
      this.aspect.set(viewerAspect / imageAspect, 1.0);
    } else {
      this.aspect.set(1.0, imageAspect / viewerAspect);
    }
  }

  resize(width,height) {
    this.viewerWidth = width;
    this.viewerHeight = height;
    this.calculateAspect();
    this.layer.scale.set(1 / this.aspect.x, 1 / this.aspect.y, 1);
    this.resizeText();
  }

  resizeText() {
    if (this.sloganText) {
      const viewerAspect = this.viewerWidth / this.viewerHeight;
      this.sloganText.scale.set(1,viewerAspect,1);
    }
  }

  dispose() {
    this.group.removeFromParent();
    this.layer.geometry.dispose();
    this.material.uniforms.currentSlide.value.dispose();
    this.material.dispose();
    if (this.sloganText) {
      this.sloganText.dispose();
    }
  }

  play() {
    const timeLine = gsap.timeline();
    const time = {value: 0};
    const timeFadeIn = {value: 0};
    const timeFadeOut = {value: 1};

    const zoomAnimation = this.params.forceAnimation ?
      gsap.to(time,{
      value: this.params.zoomDuration,
      duration: this.params.slideDuration,
      ease: 'linear',
      onUpdate: () => {
        this.sliderZoom = time.value;
      }
    }) : null;

    const fadeIn = gsap.to(timeFadeIn,{
      value: 1,
      ease: 'linear',
      duration: this.params.fadeDuration,
      onUpdate: () => {
        this.alpha = timeFadeIn.value;
      }
    });

    const fadeOut = gsap.to(timeFadeOut,{
      value: 0,
      ease: 'linear',
      duration: this.params.fadeDuration,
      onUpdate: () => {
        this.alpha = timeFadeOut.value;
      }
    });

    if (this.sloganText) {
      const textDuration = this.params.text.animation.textDuration;
      const textFadeValue = {value: 0};
      const letterSpacingValue = {value: 0};
      const textFadeIn = gsap.to(textFadeValue,{
        value: 1,
        ease: 'linear',
        duration: textDuration,
        onUpdate: () => {
          this.sloganText.fillOpacity = textFadeValue.value;
          this.sloganText.outlineOpacity = textFadeValue.value;
          this.sloganText.sync()
        },
        onStart: () => {
          if (!this.params.forceAnimation) {
            this.sloganText.curveRadius = 1.57;
            this.sloganText.sync();
          }
        }
      });

      const letterSpacing = this.params.forceAnimation ?
        gsap.to(letterSpacingValue,{
        value: 1.57,
        ease: 'linear',
        duration: textDuration,
        onUpdate: () => {
            this.sloganText.curveRadius = letterSpacingValue.value;
            this.sloganText.sync();
        }
      }) : null;

      const textFadeOut = gsap.to(textFadeValue,{
        value: 0,
        ease: 'linear',
        duration: textDuration,
        onUpdate: () => {
          this.sloganText.fillOpacity = textFadeValue.value;
          this.sloganText.outlineOpacity = textFadeValue.value;
          this.sloganText.sync();
        },
        onComplete: () => {
          this.sloganText.curveRadius = 0;
          this.sloganText.sync();
        }
      });
      timeLine.add(textFadeIn,this.params.fadeDuration);
      if (this.params.forceAnimation)
        timeLine.add(letterSpacing,this.params.fadeDuration);
      timeLine.add(textFadeOut,this.params.slideDuration - textDuration);
    }

    timeLine.add(fadeIn,0);
    if (this.params.forceAnimation)
      timeLine.add(zoomAnimation,0);
    timeLine.add(fadeOut,this.params.slideDuration);

    return timeLine;
  }

  set sliderZoom(value) {
    this.material.uniforms.uKenburnsValue.value = value;
  }

  set texture(texture) {
    this.material.uniforms.currentSlide.value = texture;
  }

  set slideDirection(number) {
    this.material.uniforms.uKenburnsDirection.value = number;
  }

  set alpha(value) {
    this.material.uniforms.uAlpha.value = value;
  }

  set position(value) {
    this.group.position.z = value;
  }

  set textFade(value) {
    // this.sloganText.
  }

  get slideGroup() {
    return this.group;
  }
}


function getRandomKenbruns () {
  const random = Math.floor(Math.random() * kenburns.length);
  return kenburns[random];
}

function replace (tag){
  const dictionary = {
    '</br>': '\n',
    '<b>': '',
    '</b>': ''
  };

  if (dictionary[tag]) {
    return dictionary[tag];
  } else {
    return '';
  }
}
